import { useState, useEffect } from "react";
import Navbar from "../Navbar/index.js";
import useSocketStore from "../../../stores/socket.js";
import usePartyStore from "../../../stores/party.js";
import Instructions from "./instructions.js";
import Standings from "./standings.js";
import { gameIndex } from "@tumbodash/clientGameEngine";
import { log } from "@tumbodash/browserPackages";
import Player from "../../../modules/player/index.js";

function Preview() {
	log.fx("(view:party:Preview)");
	const socket = useSocketStore.getState().socket!;
	const party = usePartyStore.getState().party!;
	const [view, setView] = useState<"instructions" | "standings">("instructions");
	const [isLandscape, setIsLandscape] = useState(true);
	const [skipNotice, setSkipNotice] = useState<string | null>(null);

	const player = party.players[Player.getActiveId()!];
	const playerReady = () => socket.emit("player:isReady", { emitCode: 62620 });
	const playerSkip = () => socket.emit("player:skipPreview");

	const gameDetail = Object.values(gameIndex).find(g => g.slug === party.rounds[party.activeRoundId].gameSlug);

	function handleOrientationChange() {
		setTimeout(() => {
			setIsLandscape(window.innerWidth > window.innerHeight);
		}, 50);
	}

	useEffect(() => {
		window.addEventListener("orientationchange", handleOrientationChange);
		return () => window.removeEventListener("orientationchange", handleOrientationChange);
	}, []);

	useEffect(() => {
		function onSkipped({ playerName, gameName }: { playerName: string; gameName: string }) {
			setSkipNotice(`${playerName} skipped ${gameName} (−3 pts)`);
			setTimeout(() => setSkipNotice(null), 4000);
		}
		socket.on("preview:skipped", onSkipped);
		return () => { socket.off("preview:skipped", onSkipped); };
	}, [socket]);

	const tabs = ["instructions", "standings"] as const;

	return (
		<div style={{ minHeight: "100dvh", display: "flex", flexDirection: "column", background: "var(--bg-base)" }}>
			<Navbar party={party}>
				{player.isReady ? (
					<div style={{
						display: "flex",
						alignItems: "center",
						gap: "var(--space-2)",
						color: "#4ec9b7",
						fontWeight: 700,
						fontSize: "0.9rem",
					}}>
						<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
							<path d="M20 6L9 17l-5-5" />
						</svg>
						Ready
					</div>
				) : (
					<div style={{ display: "flex", alignItems: "center", gap: "var(--space-2)" }}>
						{!player.hasSkipped && (
							<button
								onClick={playerSkip}
								style={{
									padding: "var(--space-2) var(--space-3)",
									borderRadius: "var(--radius-pill)",
									border: "1px solid var(--border)",
									background: "transparent",
									color: "var(--text-secondary)",
									fontWeight: 600,
									fontSize: "0.8rem",
									fontFamily: "var(--font-ui)",
									cursor: "pointer",
									letterSpacing: "0.01em",
								}}
							>
								Skip (−3 pts)
							</button>
						)}
						<button
							onClick={isLandscape ? playerReady : undefined}
							style={{
								padding: "var(--space-2) var(--space-4)",
								borderRadius: "var(--radius-pill)",
								border: "none",
								background: isLandscape ? "var(--accent)" : "var(--bg-elevated)",
								color: isLandscape ? "#fff" : "var(--text-muted)",
								fontWeight: 700,
								fontSize: "0.88rem",
								fontFamily: "var(--font-ui)",
								cursor: isLandscape ? "pointer" : "default",
								boxShadow: isLandscape ? "0 2px 10px rgba(255,107,107,0.35)" : "none",
								letterSpacing: "0.01em",
								transition: "background 200ms, color 200ms, box-shadow 200ms",
							}}
						>
							Ready
						</button>
					</div>
				)}
			</Navbar>

			{/* Skip notice */}
			{skipNotice && (
				<div style={{
					display: "flex",
					alignItems: "center",
					gap: "var(--space-2)",
					padding: "var(--space-2) var(--space-4)",
					background: "var(--bg-elevated)",
					borderBottom: "1px solid var(--border)",
					color: "var(--text-secondary)",
					fontSize: "0.82rem",
					fontWeight: 600,
				}}>
					<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
						<polygon points="5 3 19 12 5 21 5 3" />
					</svg>
					{skipNotice}
				</div>
			)}

			{/* Landscape warning */}
			{!isLandscape && (
				<div style={{
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					gap: "var(--space-2)",
					padding: "var(--space-2) var(--space-4)",
					background: "var(--accent-muted)",
					borderBottom: "1px solid rgba(255,107,107,0.2)",
					color: "var(--accent)",
					fontSize: "0.82rem",
					fontWeight: 600,
				}}>
					<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
						<path d="M4 8V4m0 0h4M4 4l5 5M20 8V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5M20 16v4m0 0h-4m4 0l-5-5"/>
					</svg>
					Rotate to landscape to play
				</div>
			)}

			<div style={{
				flex: 1,
				display: "flex",
				flexDirection: "column",
				gap: "var(--space-5)",
				padding: "var(--space-5) var(--space-4)",
				maxWidth: 480,
				width: "100%",
				margin: "0 auto",
			}}>

				{/* ── Game info ── */}
				<div style={{
					background: "var(--bg-surface)",
					border: "1px solid var(--border)",
					borderRadius: "var(--radius-lg)",
					padding: "var(--space-4)",
				}}>
					<div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "var(--space-3)" }}>
						<div style={{
							fontSize: "1.3rem",
							fontWeight: 800,
							letterSpacing: "-0.02em",
							color: "var(--text-primary)",
							lineHeight: 1.2,
						}}>
							{gameDetail.name}
						</div>
						<div style={{
							flexShrink: 0,
							background: "var(--reward-muted)",
							border: "1px solid rgba(255,230,109,0.2)",
							borderRadius: "var(--radius-pill)",
							padding: "var(--space-1) var(--space-3)",
							color: "var(--reward)",
							fontSize: "0.72rem",
							fontWeight: 700,
							letterSpacing: "0.06em",
							textTransform: "uppercase",
							whiteSpace: "nowrap",
						}}>
							Round {party.activeRound}
						</div>
					</div>
				</div>

				{/* ── Tabs + content ── */}
				<div>
					{/* Tab bar */}
					<div style={{
						display: "flex",
						borderBottom: "1px solid var(--border)",
						marginBottom: "var(--space-4)",
					}}>
						{tabs.map(tab => (
							<button
								key={tab}
								onClick={() => setView(tab)}
								style={{
									padding: "var(--space-2) var(--space-4)",
									background: "transparent",
									border: "none",
									borderBottom: view === tab ? "2px solid var(--accent)" : "2px solid transparent",
									color: view === tab ? "var(--accent)" : "var(--text-secondary)",
									fontWeight: 600,
									fontSize: "0.875rem",
									fontFamily: "var(--font-ui)",
									cursor: "pointer",
									letterSpacing: "0.02em",
									transition: "color 120ms, border-color 120ms",
									marginBottom: -1,
								}}
							>
								{tab.charAt(0).toUpperCase() + tab.slice(1)}
							</button>
						))}
					</div>

					{/* Tab content */}
					{view === "instructions" ? <Instructions /> : <Standings />}
				</div>

			</div>
		</div>
	);
}

export default Preview;
